import { useQuery, gql } from '@apollo/client';
import React, { createContext, useContext, ReactNode } from 'react';

const CLIENT_CONFIG_QUERY = gql`
    query ClientConfig {
        clientConfig {
            name
            slug
            logoUrl
            faviconUrl
            primaryColor
            secondaryColor
            currency
            timezone
            defaultLanguage
            features
        }
    }
`;

export interface ClientConfig {
    name: string;
    slug: string;
    logoUrl: string;
    faviconUrl: string;
    primaryColor: string;
    secondaryColor: string;
    currency: string;
    timezone: string;
    defaultLanguage: string;
    features: Record<string, boolean>;
}

interface ClientContextValue {
    config: ClientConfig | null;
    loading: boolean;
    error: Error | undefined;
}

const defaultConfig: ClientConfig = {
    name: 'Club zum Rennweg',
    slug: 'czr',
    logoUrl: '/images/logo.png',
    faviconUrl: '/favicon.ico',
    primaryColor: '#1a1a1a',
    secondaryColor: '#c9a96e',
    currency: 'CHF',
    timezone: 'Europe/Zurich',
    defaultLanguage: 'de',
    features: {
        events: true,
        reservations: true,
        member_search: true,
        digital_member_card: false,
        qr_business_card: true,
        linkedin_integration: true,
        profile_approval: true,
        consumption_invoices: true,
        accounting_export: false,
        member_import: true,
        admin_2fa: true,
    },
};

const ClientContext = createContext<ClientContextValue>({
    config: defaultConfig,
    loading: false,
    error: undefined,
});

interface ClientProviderProps {
    children: ReactNode;
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
    const { data, loading, error } = useQuery(CLIENT_CONFIG_QUERY, {
        fetchPolicy: 'cache-first',
    });

    const config = data?.clientConfig || defaultConfig;

    return (
        <ClientContext.Provider value={{ config, loading, error }}>
            {children}
        </ClientContext.Provider>
    );
};

/**
 * Hook to access the full client context
 */
export const useClientContext = () => useContext(ClientContext);

/**
 * Hook to access just the client config
 */
export const useClient = (): ClientConfig | null => {
    const { config } = useContext(ClientContext);
    return config;
};

/**
 * Hook to check if a specific feature is enabled
 * 
 * @param featureName - The name of the feature to check
 * @returns boolean - Whether the feature is enabled
 * 
 * @example
 * const hasReservations = useFeature('reservations');
 * if (hasReservations) {
 *   // Show reservations UI
 * }
 */
export const useFeature = (featureName: string): boolean => {
    const { config } = useContext(ClientContext);
    return config?.features?.[featureName] ?? false;
};

/**
 * Hook to get the client name
 */
export const useClientName = (): string => {
    const { config } = useContext(ClientContext);
    return config?.name ?? 'Club';
};

/**
 * Hook to get the client currency
 */
export const useClientCurrency = (): string => {
    const { config } = useContext(ClientContext);
    return config?.currency ?? 'CHF';
};

export default ClientContext;
